import { useContext, useCallback } from "react"
import styles from "./themePicker.module.css"
import { Palette } from "lucide-react"
import { TemplateContext } from "../../../../../../../../contexts/TemplateContext"
import type { Template } from "../../../../../../../../sdkStructs"

const colorOptions = [
  "#4B5563",
  "#6B7280",
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#EAB308",
  "#84CC16",
  "#22C55E",
  "#10B981",
  "#14B8A6",
  "#07d3fa",
  "#0e6ba8",
  "#3B82F6",
  "#4F46E5",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#D946EF",
  "#EC4899",
]

interface ColorInputProps {
  label: string
  value: string
  onChange: (value: string) => void
}

const ColorInput = ({ label, value, onChange }: ColorInputProps) => {
  return (
    <div className={styles.colorInput}>
      <label>{label}</label>
      <div className={styles.colorInputWrapper}>
        <div className={styles.colorPreview} style={{ backgroundColor: value }} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  )
}

export default function ThemeColorPicker() {
  const { template, setTemplate } = useContext(TemplateContext)

  const handleColorChange = useCallback(
    (colorType: "PrimaryColor" | "SecondaryColor" | "BackgroundColor", color: string) => {
      setTemplate((prevTemplate: Template | null) => {
        if (!prevTemplate) return null;
        return {
          ...prevTemplate,
          [colorType]: color,
        };
      })
    },
    [setTemplate],
  )

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Palette className={styles.icon} />
        <h1>Theme</h1>
      </div>

      <div className={styles.colorGrid}>
        {colorOptions.map((color) => (
          <button
            key={color}
            className={`${styles.colorSwatch} ${template?.PrimaryColor === color ? styles.selected : ""}`}
            style={{ backgroundColor: color }}
            onClick={() => handleColorChange("PrimaryColor", color)}
            aria-label={`Select color ${color}`}
          />
        ))}
      </div>

      <div className={styles.inputs}>
        <ColorInput
          label="Primary Color"
          value={template?.PrimaryColor || ""}
          onChange={(color) => handleColorChange("PrimaryColor", color)}
        />
        <ColorInput
          label="Secondary Color"
          value={template?.SecondaryColor || ""}
          onChange={(color) => handleColorChange("SecondaryColor", color)}
        />
        <ColorInput
          label="Background Color"
          value={template?.BackgroundColor || ""}
          onChange={(color) => handleColorChange("BackgroundColor", color)}
        />
      </div>
    </div>
  )
}

